import { AIInsight } from "@coeff/agent-types";
import React, { useEffect, useState } from "react";

import { DashboardWindow } from "./dashboardHelpers";
import { Theme, useTheme } from "./themes";

type ParsedAIInsight = AIInsight & {
  description_html?: string;
};

interface Props {
  blockId: string;
  title: string;
  gradientFrom: string;
  gradientTo: string;
  gradientDirection: string;
  insights: AIInsight[];
  targetWindow: DashboardWindow;
  idSuffix?: string;
}

const GRADIENT_DIRECTION_CLASS_MAP: Record<string, string> = {
  "to-r": "bg-gradient-to-r",
  "to-br": "bg-gradient-to-br",
  "to-bl": "bg-gradient-to-bl",
  "to-tr": "bg-gradient-to-tr",
  "to-l": "bg-gradient-to-l",
  "to-tl": "bg-gradient-to-tl",
};

const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // Constrain to 32-bit integer
  }
  return hash;
};

// Choose a random background gradient from theme. The chosen background gradient is stable
// for a given blockId and date.
const chooseThemeBackgroundGradient = (theme: Theme, blockId: string): string => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Use round() and abs() to ensure the hash is a non-negative integer
  const hash = Math.abs(Math.round(hashString(blockId + today.toISOString())));

  const index = hash % theme.tailwind.general.cardBackgroundGradients.length;
  return theme.tailwind.general.cardBackgroundGradients[index];
};

export const AIInsightsBlock: React.FC<Props> = ({
  blockId,
  title,
  gradientFrom,
  gradientTo,
  gradientDirection,
  insights,
  targetWindow,
  idSuffix = "",
}) => {
  const fullBlockId = `block_${blockId}${idSuffix ? `_${idSuffix}` : ""}`;
  const gradientDirectionClass =
    GRADIENT_DIRECTION_CLASS_MAP[gradientDirection] || "bg-gradient-to-r";

  const [parsedInsights, setParsedInsights] = useState<ParsedAIInsight[]>(insights);

  const { theme } = useTheme();

  // Parse insight markdown descriptions and convert to HTML
  useEffect(() => {
    if (targetWindow.marked) {
      const parsed = insights.map(insight => ({
        ...insight,
        description_html: targetWindow.marked.parse(insight.description),
      }));

      setParsedInsights(parsed);
    }
  }, [insights, targetWindow.marked]);

  const backgroundGradient = chooseThemeBackgroundGradient(theme, fullBlockId);

  // Use hsl interpolation for theme gradients to create smoother, more natural gradients
  const blendedGradientDiv = (
    <div className="isolate absolute w-full h-full left-0 top-0 -z-1">
      <div
        className={`${gradientDirectionClass} from-${gradientFrom} to-${gradientTo} w-full h-full mix-blend-normal absolute left-0 top-0`}
      />
      <div
        className={`bg-linear-to-bl/hsl ${backgroundGradient} w-full h-full mix-blend-hue absolute left-0 top-0 opacity-80`}
      />
      <div
        className={`bg-linear-to-bl/hsl ${backgroundGradient} w-full h-full mix-blend-lighten absolute left-0 top-0 opacity-80`}
      />
    </div>
  );

  return (
    <div
      id={fullBlockId}
      className="rounded-themed p-6 h-full flex flex-col shadow-card relative overflow-hidden"
    >
      {blendedGradientDiv}
      <h2 className="text-lg font-semibold text-white text-shadow-xs mb-6 flex-shrink-0">
        {title}
      </h2>
      <div
        className="grid grid-cols-2 gap-4 flex-1 min-h-0 overflow-auto auto-rows-fr"
        data-sentry-block
      >
        {parsedInsights.map((insight, index) => (
          <div key={index} className="bg-main-background rounded-themed">
            <div className="bg-card-background text-card-foreground p-4 flex items-center rounded-themed w-full h-full">
              <div className="flex items-start gap-3">
                <span className={`material-icons text-3xl ${insight.icon_color} flex-shrink-0`}>
                  {insight.icon}
                </span>
                <div className="min-w-0">
                  <h3 className="font-semibold mb-1">{insight.title}</h3>
                  <div
                    className="text-sm text-card-muted-foreground insight-description"
                    dangerouslySetInnerHTML={{ __html: insight.description_html ?? "" }}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
